import { Banner, Button, Field, Input, Select, Switch } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';

export interface VoiceFields { voice: { stt: boolean; tts: boolean; voiceId?: string; credentialId?: string } | null }
const PLATFORM_OPTION = { value: '', label: 'Chave da plataforma' };
interface Cred { id: string; name: string; provider: string; hint: string | null }

/** Aba Voz do editor: microfone (Whisper, sem custo extra de chave) e botão Ouvir (ElevenLabs com a chave da organização) */
export function AgentVoiceFields({ value, readOnly, onChange }: { value: VoiceFields; readOnly?: boolean; onChange: (patch: VoiceFields) => void }) {
  const fetchApi = useAuthenticatedFetch();
  const [creds, setCreds] = useState<Cred[]>([]);
  const [vaultReady, setVaultReady] = useState(true);
  const [platformVoice, setPlatformVoice] = useState(false);
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const v = value.voice ?? { stt: false, tts: false };
  const set = (patch: Partial<NonNullable<VoiceFields['voice']>>) => onChange({ voice: { ...v, ...patch } });

  const load = useCallback(async () => {
    const r = await fetchApi('/api/ai/credentials');
    if (!r.ok) return;
    const j = await r.json();
    setVaultReady(j.vaultReady !== false);
    setPlatformVoice(!!j.platformVoice);
    setCreds((j.credentials as Cred[]).filter((c) => c.provider === 'elevenlabs'));
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const addKey = async () => {
    setError('');
    const r = await fetchApi('/api/ai/credentials', { method: 'POST', body: JSON.stringify({ name: `ElevenLabs ${new Date().toLocaleDateString('pt-BR')}`, value: key.trim(), provider: 'elevenlabs' }) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setError(j.message || 'Não foi possível guardar a chave'); return; }
    setKey('');
    await load();
    set({ credentialId: j.credential.id });
  };

  return (
    <div className="space-y-4">
      <Field label="Microfone" description="O visitante fala e a fala vira texto (Whisper, no Workers AI; entende português).">
        <Switch onCheckedChange={(checked: boolean) => set({ stt: checked })} checked={v.stt} disabled={readOnly} label={<>Permitir falar com o agente</>} />
      </Field>
      <Field label="Ouvir" description="Botão para ouvir a resposta, com a voz escolhida. Usa a ElevenLabs, com a chave da plataforma ou a sua (o TTS do Workers AI não cobre português).">
        <Switch onCheckedChange={(checked: boolean) => set({ tts: checked })} checked={v.tts} disabled={readOnly} label={<>Ler as respostas em voz alta</>} />
      </Field>
      {v.tts && (
        <>
          <Field label="ID da voz" description="Copie na biblioteca de vozes da ElevenLabs (escolha uma voz pt-BR).">
            <Input onChange={(e) => set({ voiceId: e.target.value })} value={v.voiceId ?? ''} disabled={readOnly} placeholder="21m00Tcm4TlvDq8ikWAM" />
          </Field>
          <Field label="Chave da ElevenLabs" description={platformVoice ? 'Por padrão vale a chave da plataforma. Escolha uma própria se quiser usar a sua conta (fica cifrada no cofre).' : 'Fica cifrada no cofre; nunca volta para a tela.'}>
            <Select value={(creds.filter((c) => c.id === v.credentialId).map((c) => ({ value: c.id, label: `${c.name} (${c.hint ?? '••••'})` }))[0]
                ?? (platformVoice ? PLATFORM_OPTION : null))?.value ?? null} items={[...(platformVoice ? [PLATFORM_OPTION] : []), ...creds.map((c) => ({ value: c.id, label: `${c.name} (${c.hint ?? '••••'})` }))]} onValueChange={(value: string | null) => set({ credentialId: value || undefined })} disabled={readOnly} placeholder="Escolha uma chave" />
          </Field>
          {!readOnly && (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Field label="Nova chave">
                  <Input onChange={(e) => setKey(e.target.value)} type="password" value={key} placeholder="Cole a API key da ElevenLabs" />
                </Field>
              </div>
              <Button variant="secondary" disabled={!key.trim() || !vaultReady} onClick={addKey}>Guardar no cofre</Button>
            </div>
          )}
          {!vaultReady && <Banner variant="alert" description={<>O cofre de credenciais não está configurado neste ambiente.</>} />}
          {error && <Banner variant="error" description={<>{error}</>} />}
        </>
      )}
      <div className="text-xs !text-[var(--text-muted)]">O agente sempre se apresenta como assistente virtual (IA). O microfone e o botão Ouvir aparecem no widget depois de publicar a versão.</div>
    </div>
  );
}
